/**
 * Where a page starts when you arrive at it — including when you arrive by
 * pressing Back.
 *
 * ## The gap this closes
 *
 * The shell scrolls in `<main>`, not the window, so neither the browser's own
 * scroll restoration nor the router's (both of which target the window) reaches
 * it. What the shell had instead was an unconditional scroll-to-top on every
 * path change, which is right for a link and wrong for the Back button:
 * measured on `/transactions`, scrolling 482px down the list, opening a
 * transaction and pressing Back landed the reader at row one every time. In a
 * tool whose whole loop is "scan a list → open one → come back → open the next",
 * that loses the reader's place on every single pass.
 *
 * ## The rule
 *
 * Three cases, and they are genuinely different:
 *
 * - **Same path, new search params.** Not an arrival at all — a tab strip or a
 *   chain selector rewriting the URL of the page you are already reading. Leave
 *   the scroll alone; yanking someone to the top when they pick a tab is its own
 *   bug, and it is why the reset was keyed on the path in the first place.
 * - **A history entry we have seen before.** Back or Forward. Restore what was
 *   there when it was left.
 * - **Anything else.** A fresh push. Start at the top.
 *
 * Keyed on the history entry, not on the URL: two visits to `/transactions` are
 * two places in the reader's history, and the second must not inherit where the
 * first was left.
 *
 * ## Why restoring needs more than one attempt
 *
 * Every page in this app is a lazy route whose content then loads asynchronously,
 * so at the moment of the location change `<main>` is a spinner a few hundred
 * pixels tall — `scrollTop = 482` on it silently clamps to 0, and the reader
 * lands at the top anyway, which is the bug the fix was for. {@link restoreScroll}
 * therefore keeps asking until the container is tall enough to honour the offset
 * or the deadline passes, and stops the moment the reader scrolls for themselves.
 */

/** The offsets we remember, keyed by history entry. */
export type ScrollMemory = Map<string, number>;

/**
 * The history entry's own identity, which is what makes "have I been here
 * before" answerable.
 *
 * TanStack stores a per-entry key in `location.state.__TSR_key` (the same field
 * its own scroll restoration keys on). Before the first navigation there is no
 * key yet, and `href` is the honest fallback: on a first paint there is nothing
 * remembered under it either way.
 */
export function scrollKeyOf(location: {
  href: string;
  state?: { __TSR_key?: string } | undefined;
}): string {
  return location.state?.__TSR_key || location.href;
}

export type ArrivalKind = "stay" | "restore" | "top";

/**
 * What should happen to the scroll position on this location change.
 *
 * Pure, so the three cases can be stated as facts rather than driven through a
 * router and a DOM.
 */
export function arrivalKind(input: {
  previousPathname: string | null;
  nextPathname: string;
  remembered: number | undefined;
}): ArrivalKind {
  if (input.previousPathname === input.nextPathname) return "stay";
  if (input.remembered !== undefined && input.remembered > 0) return "restore";
  return "top";
}

/**
 * The hard cap on retrying. Generous, because the loop below stops on its own as
 * soon as the page stops growing — this is only the backstop for a page that
 * never settles (a live feed appending rows forever).
 */
export const RESTORE_DEADLINE_MS = 5000;

/**
 * How long the scrollable height must hold still before we accept that the
 * offset is out of reach.
 *
 * A fixed deadline alone was wrong, and measurably so: opening an entity from a
 * transaction and pressing Back restored **85 of 900px**, because the
 * transaction page — lazy route, async body, decoded tabs, a graph — was still
 * at its first-paint height when the timer ran out, and `scrollTop` silently
 * clamps. "Still growing" is the real signal; the clock is only there so a feed
 * that never settles cannot retry forever.
 *
 * The window is generous because these pages load in STAGES, with real pauses
 * between them — a 600ms window stopped inside the first gap and never resumed,
 * which is the same 85px. It is also why the deadline is not longer than it is:
 * past a few seconds a reader has started reading, and being moved then is worse
 * than being left at the top.
 */
export const RESTORE_SETTLE_MS = 1000;

export interface RestoreScrollOptions {
  element: HTMLElement;
  top: number;
  deadlineMs?: number;
  settleMs?: number;
  /** Injected in tests; defaults to `requestAnimationFrame` over `Date.now`. */
  now?: () => number;
  schedule?: (fn: () => void) => void;
}

/**
 * Scroll `element` to `top`, retrying while the page fills out.
 *
 * Returns a cancel function, which the caller MUST run when the location
 * changes again — a restore still retrying into the previous page's offset
 * would fight the next page's own arrival.
 *
 * Stops on four conditions, all of them "the answer is already known": the
 * offset was reached; the reader has scrolled somewhere themselves, so the
 * remembered position is no longer what they want; the container's height has
 * held still long enough that the clamp is the real end of the page rather than
 * a half-loaded one; or the hard deadline, for a page that never settles.
 */
export function restoreScroll(options: RestoreScrollOptions): () => void {
  const { element, top } = options;
  const deadlineMs = options.deadlineMs ?? RESTORE_DEADLINE_MS;
  const settleMs = options.settleMs ?? RESTORE_SETTLE_MS;
  const now = options.now ?? (() => Date.now());
  // A frame OR a timer, whichever comes first — never a frame alone.
  //
  // `requestAnimationFrame` does not fire at all while a tab is hidden, and a
  // tab is hidden in exactly the case this loop is for: a page opened in the
  // background, or one the reader left mid-load and came back to. The retry
  // simply stopped, leaving the reader at whatever the first paint clamped to.
  // (Found while measuring this in a hidden browser pane, which is the same
  // condition — the restore stalled at 85 of 900px and never resumed.)
  const schedule =
    options.schedule ??
    ((fn: () => void) => {
      let done = false;
      const once = (): void => {
        if (done) return;
        done = true;
        fn();
      };
      if (typeof requestAnimationFrame === "function") requestAnimationFrame(once);
      setTimeout(once, 64);
    });

  // Scroll anchoring OFF for the duration.
  //
  // Chrome keeps the visible content stable when things load ABOVE the scroll
  // position — which is precisely what a restoring page does. Measured here: the
  // restore reached 900px correctly, then ~5,000px of decoded content mounted
  // above it and the browser carried the reader down to 5,848 to "keep their
  // place". Fighting that from inside the loop is not possible, because an
  // anchored shift and a reader's own scroll look identical from `scrollTop`;
  // turning it off for the second or two the restore takes is.
  const hadAnchor = element.style.getPropertyValue("overflow-anchor");
  element.style.setProperty("overflow-anchor", "none");
  const releaseAnchor = (): void => {
    if (hadAnchor) element.style.setProperty("overflow-anchor", hadAnchor);
    else element.style.removeProperty("overflow-anchor");
  };

  const started = now();
  let cancelled = false;
  // What we last set. A reader who scrolls during the retry window leaves
  // `scrollTop` somewhere we did not put it, which is how we notice.
  let lastSet: number | null = null;
  // The last height we saw, and when it last changed — the "is the page still
  // filling out?" signal that replaced a bare timeout.
  let lastHeight = -1;
  let heightChangedAt = started;

  const finish = (): void => {
    releaseAnchor();
  };

  const attempt = (): void => {
    if (cancelled) return;
    if (lastSet !== null && Math.abs(element.scrollTop - lastSet) > 1) return finish();

    const height = element.scrollHeight;
    const at = now();
    if (height !== lastHeight) {
      lastHeight = height;
      heightChangedAt = at;
    }

    element.scrollTop = top;
    lastSet = element.scrollTop;

    const reached = Math.abs(element.scrollTop - top) <= 1;
    // Settled: the page has stopped growing, so this is either the offset or
    // genuinely as far as the page goes. Reaching the offset early is NOT enough
    // on its own — more content may still arrive and move it.
    if (at - heightChangedAt >= settleMs) return finish();
    if (at - started >= deadlineMs) return finish();
    void reached;
    schedule(attempt);
  };

  attempt();
  return () => {
    cancelled = true;
    releaseAnchor();
  };
}
